import { useCallback, useMemo } from 'react';

import {
  TableRowActionsMenu,
  ConfirmDialog,
  Typography,
  useConfirmDialog,
} from '@occc/ui-design-system';
import { snackbar } from '@occc-app/design-system';

import { useDeleteSegment } from '../../hooks/segments.mutation.hook';

type SegmentListActionsControllerProps = {
  uuid: string;
  name: string;
  isDeleteAllowed: boolean;
};

const onDeleteCompleted = () => snackbar.success('Segment has been successfully deleted.');

export const SegmentListActionsController = ({
  uuid,
  name,
  isDeleteAllowed,
}: SegmentListActionsControllerProps) => {
  const [segmentDelete] = useDeleteSegment({
    onCompleted: onDeleteCompleted,
    refetchQueries: ['segments'],
  });

  const onDelete = useCallback(
    () => segmentDelete({ variables: { id: uuid } }),
    [segmentDelete, uuid],
  );

  const { open, setOpen, onCloseButtonClick, onConfirmButtonClick } = useConfirmDialog({
    onConfirm: onDelete,
    shouldStopEventPropagation: true,
  });

  const menuItems = useMemo(() => {
    const items = [];

    if (isDeleteAllowed) {
      items.push({
        label: 'Delete Segment',
        onClick: () => setOpen(true),
        dataTestId: 'delete',
      });
    }

    return items;
  }, [setOpen, isDeleteAllowed]);

  return (
    <>
      <TableRowActionsMenu menuItems={menuItems} />
      <ConfirmDialog
        dialogTitle='Are you sure?'
        open={open}
        onClose={onCloseButtonClick}
        confirmButtonText='Confirm'
        confirmButtonProps={{
          onClick: onConfirmButtonClick,
          color: 'error',
        }}
      >
        <Typography>This action will delete the Segment "{name}".</Typography>
      </ConfirmDialog>
    </>
  );
};
